import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import { getSocket } from "../../utils/socket";
import { getImageUrl } from "../../utils/imageUtils";
import { toLocalizedNumber } from "../../utils/localeUtils";
import {
  FaBell,
  FaBox,
  FaMoneyBillWave,
  FaComment,
  FaStar,
  FaShoppingCart,
  FaCog,
  FaTrash,
} from "react-icons/fa";
import Pagination from "../../components/Pagination";
import "./Notifications.css";

const PER_PAGE = 10;

const typeIcons = {
  order: FaBox,
  payment: FaMoneyBillWave,
  message: FaComment,
  review: FaStar,
  product: FaShoppingCart,
  system: FaCog,
};

const NotificationPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const u = localStorage.getItem("user");
    if (!u) {
      navigate("/login");
      return;
    }
    try {
      setUser(JSON.parse(u));
    } catch (_) {
      navigate("/login");
    }
  }, [navigate]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/notifications?limit=500");
      const list = Array.isArray(data?.notifications) ? data.notifications : [];
      setNotifications(list);
      setUnreadCount(
        typeof data?.unreadCount === "number" ? data.unreadCount : 0,
      );
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Real-time: ensure socket exists and append new notifications via socket
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    if (token) getSocket(); // ensure socket is created when user is logged in
    const socket = getSocket();
    if (!socket) return;
    const onNotification = (payload) => {
      const id = payload._id || payload.id;
      setNotifications((prev) => {
        if (id && prev.some((n) => (n._id || n.id) === id)) return prev;
        return [
          {
            _id: id,
            type: payload.type,
            title: payload.title,
            message: payload.message,
            link: payload.link,
            image: payload.image,
            entityName: payload.entityName,
            isRead: payload.isRead ?? false,
            createdAt: payload.createdAt,
          },
          ...prev,
        ];
      });
      setUnreadCount((prev) => prev + 1);
    };
    socket.on("notification", onNotification);
    return () => socket.off("notification", onNotification);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleMarkAsRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Mark read failed", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Mark all read failed", err);
    }
  };

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const notif = notifications.find((n) => n._id === id);
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      if (notif && !notif.isRead)
        setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Delete notification failed", err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return t("notifications.justNow");
    if (diffMins < 60)
      return t("notifications.minutesAgo", {
        minutes: toLocalizedNumber(diffMins),
      });
    if (diffHours < 24)
      return t("notifications.hoursAgo", {
        hours: toLocalizedNumber(diffHours),
      });
    if (diffDays < 7)
      return t("notifications.daysAgo", { days: toLocalizedNumber(diffDays) });
    return d.toLocaleDateString();
  };

  const typeIcon = (type) => {
    const Icon = typeIcons[type] || FaBell;
    return <Icon className="notif-page-type-icon" />;
  };

  const totalPages = Math.ceil(notifications.length / PER_PAGE);
  const pageNotifications = notifications.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  if (!user) return null;

  return (
    <div className="notifications-page">
      <Header />
      <main className="notifications-page-main">
        <div className="notifications-page-container">
          <div className="notifications-page-header">
            <h1 className="notifications-page-title">
              {t("notifications.title")}
            </h1>
            <p className="notifications-page-subtitle">
              {t("notifications.subtitle")}
            </p>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notifications-page-mark-all"
                onClick={handleMarkAllRead}
              >
                {t("notifications.markAllAsRead")}
              </button>
            )}
          </div>
          {loading ? (
            <div className="notifications-page-loading">
              <div className="notifications-page-spinner" />
              <p>{t("notifications.loading")}</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notifications-page-empty">
              <FaBell className="notifications-page-empty-icon" />
              <p>{t("notifications.noNotifications")}</p>
              <p className="notifications-page-empty-hint">
                {t("notifications.emptyHint")}
              </p>
            </div>
          ) : (
            <ul className="notifications-page-list">
              {pageNotifications.map((n, idx) => (
                <li key={n._id || idx} className="notifications-page-item-wrap">
                  <Link
                    to={n.link || "#"}
                    className={`notifications-page-item ${n.isRead ? "read" : ""}`}
                    onClick={() => !n.isRead && handleMarkAsRead(n._id)}
                  >
                    {n.image ? (
                      <span className="notifications-page-item-thumb">
                        <img
                          src={getImageUrl(n.image)}
                          alt=""
                          className="notifications-page-item-img"
                        />
                      </span>
                    ) : (
                      <span className="notifications-page-item-icon">
                        {typeIcon(n.type)}
                      </span>
                    )}
                    <div className="notifications-page-item-body">
                      {n.entityName && (
                        <div className="notifications-page-item-entity">
                          {n.entityName}
                        </div>
                      )}
                      <div className="notifications-page-item-title">
                        {n.title}
                      </div>
                      <div className="notifications-page-item-message">
                        {n.message}
                      </div>
                      <div className="notifications-page-item-time">
                        {formatDate(n.createdAt)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="notifications-page-item-delete"
                      title={t("notifications.remove")}
                      onClick={(e) => handleDelete(n._id, e)}
                    >
                      <FaTrash />
                    </button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      </main>
    </div>
  );
};

export default NotificationPage;
